import {
  GeneralPermissions,
  MarketingPermissions,
} from "@redotech/account-common/permissions";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import AnnouncementIcon3 from "@redotech/redo-icons/announcement-03.svg";
import AnnouncementIcon3Filled from "@redotech/redo-icons/announcement-03_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardFilledIcon from "@redotech/redo-icons/dashboard_filled.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { LinkPrimaryTab } from "@redotech/redo-web/arbiter-components/navigation/primary/redo-primary-nav";
import { PageNavigationEntry } from "@redotech/redo-web/arbiter-components/navigation/redo-side-navigation";
import { SecondaryNavigationData } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav";
import {
  RedoSecondaryNavItem,
  useNavTreeNode,
} from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext } from "react";
import { MarketingCloudStatusesContext } from "../../../../redo-suite/marketing-cloud";
import { ReviewsToReviewContext } from "../../../../services/marketing/reviews-to-review-service";
import { AnalyticsItems } from "./analytics";
import { AutomationsItems } from "./automations";
import { ContextualItems } from "./contextual";
import { DiscountsItems } from "./discounts";
import { EmailSMSItems } from "./email-sms";
import { LoyaltyItems } from "./loyalty";
import { OrderTrackingItems } from "./order-tracking";
import { RecoverItems } from "./recover";
import { ReviewsItems } from "./reviews";

export function useMarketingNavigation(): PageNavigationEntry | null {
  const { t } = useTranslation();
  const { marketingCloudEnabled, emailSmsEnabled } = useRequiredContext(
    MarketingCloudStatusesContext,
  );

  const routes = useRequiredContext(MerchantRoutesContext);
  const pathRoot = routes.marketingBaseUrl;

  const reviewCount = useContext(ReviewsToReviewContext);

  const canViewMarketingSection = useHasPermission(
    GeneralPermissions.OTHER,
    ...Object.values(MarketingPermissions),
  );

  if (!canViewMarketingSection) {
    return null;
  }

  const hasNotification = reviewCount > 0;

  const primaryHref = routes.getMarketingHref({
    emailSmsEnabled,
    marketingCloudEnabled,
  });

  const primaryTab: LinkPrimaryTab = {
    id: "marketing",
    hasNotification,
    UnfilledIcon: AnnouncementIcon3,
    FilledIcon: AnnouncementIcon3Filled,
    type: "link",
    pathRoot,
    href: primaryHref,
    friendlyName: t("Marketing", {
      context: "primary navigation section for merchant marketing tools",
    }),
  };

  const secondaryNavigationData: SecondaryNavigationData | undefined =
    marketingCloudEnabled
      ? {
          id: "marketing",
          name: t("Marketing", {
            context: "primary navigation section for merchant marketing tools",
          }),
          NavContent: SecondaryMarketingSection,
        }
      : undefined;

  return {
    primaryTab,
    secondaryNavigationData,
    isActiveProduct: marketingCloudEnabled,
  };
}

const SecondaryMarketingSection = memo(function SecondaryMarketingSection() {
  const { t } = useTranslation();
  const { id: nodeId, TreeContext } = useNavTreeNode({
    friendlyName: t("Marketing", {
      context: "primary navigation section for merchant marketing tools",
    }),
  });

  return (
    <TreeContext.Provider value={nodeId}>
      <MarketingDashboardItem />
      <AnalyticsItems />
      <AutomationsItems />
      <ContextualItems />
      <DiscountsItems />
      <EmailSMSItems />
      <LoyaltyItems />
      <OrderTrackingItems />
      <RecoverItems />
      <ReviewsItems />
    </TreeContext.Provider>
  );
});

const MarketingDashboardItem = memo(function MarketingDashboardItem() {
  const { t } = useTranslation();
  const routes = useRequiredContext(MerchantRoutesContext);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: routes.marketingBaseUrl }}
      FilledIcon={DashboardFilledIcon}
      friendlyName={t("Dashboard", {
        context: "merchant marketing navigation label",
      })}
      id="marketing-dashboard"
      UnfilledIcon={DashboardIcon}
    />
  );
});
